import {useEffect, useMemo, useState} from 'react';
import {Link, useHistory, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import { useMetaTags } from '../../services/useMetaTags';
import trackPageView from '../../services/trackPageView';
import PromoCode from '../../components/Payment/PromoCode';
import NotFoundPage from '../Errors/NotFound';
import {parseRouteResponse} from '../../services/route-response';
import '../../assets/styles/library/libraryMaterial.scss';
import '../../assets/styles/layout/wysiwygContent.scss';
import '../../assets/styles/materialBundle/materialPage.scss';
import '../../assets/styles/materialBundle/materialThroughPromoCode.scss';

function getCookie(name) {
    const target = `${name}=`;
    const values = document.cookie ? document.cookie.split('; ') : [];

    for (let i = 0; i < values.length; i += 1) {
        if (values[i].indexOf(target) === 0) {
            return decodeURIComponent(values[i].substring(target.length));
        }
    }

    return null;
}

function removeCookie(name) {
    document.cookie = `${name}=; Max-Age=0; path=/`;
}

export default function LibraryMaterial() {
    const {t} = useTranslation();
    const history = useHistory();
    const params = useParams();
    const sectionId = params.sectionId || null;
    const id = params.id || params.sectionId;
    const [data, setData] = useState(null);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [testResult, setTestResult] = useState(null);
    const [testError, setTestError] = useState(false);
    const [bonusBuyError, setBonusBuyError] = useState(false);
    const [missionCompleteItems, setMissionCompleteItems] = useState([]);
    const [notFound, setNotFound] = useState(false);

    const requestUrl = sectionId ? `/api/v1/material/${sectionId}/${id}` : `/api/v1/material/${id}`;

    const loadMissionCompleteFromCookie = () => {
        const completedQuests = getCookie('completedQuests');
        if (!completedQuests) {
            return;
        }

        try {
            const parsed = JSON.parse(completedQuests);
            if (Array.isArray(parsed)) {
                setMissionCompleteItems(parsed);
            }
        } catch (e) {
            // Ignore invalid cookie payload.
        }

        removeCookie('completedQuests');
    };

    useEffect(() => {
        fetch(requestUrl)
            .then(async (response) => {
                const payload = await response.json().catch(() => null);
                return {response, payload};
            })
            .then(({response, payload}) => {
                const {kind, redirectUrl} = parseRouteResponse(response, payload);

                if (kind === 'not_found') {
                    setData(null);
                    setNotFound(true);
                    return;
                }

                setNotFound(false);

                if ((kind === 'unauthorized' || kind === 'redirect' || kind === 'error') && redirectUrl) {
                    history.replace(redirectUrl);
                    return;
                }

                if (kind === 'error') {
                    setData(null);
                    return;
                }

                setData(payload);
            })
            .catch(() => {
                setData(null);
            });
    }, [history, requestUrl]);

    useEffect(() => {
        if (!data?.material?.id) {
            return;
        }

        trackPageView(`Material: ${data.material.title}`);
    }, [data?.material?.id, data?.material?.title]);

    useEffect(() => {
        loadMissionCompleteFromCookie();
    }, [data?.material?.id]);

    useEffect(() => {
        if (!missionCompleteItems.length) {
            return;
        }

        const timer = setTimeout(() => setMissionCompleteItems([]), 6000);
        return () => clearTimeout(timer);
    }, [missionCompleteItems]);

    useEffect(() => {
        const finishUrl = data?.actions?.viewFinishUrl;
        if (!finishUrl) {
            return undefined;
        }

        const onUnload = () => {
            fetch(finishUrl, {method: 'POST', keepalive: true, credentials: 'same-origin'});
        };

        window.addEventListener('beforeunload', onUnload);
        return () => {
            window.removeEventListener('beforeunload', onUnload);
            onUnload();
        };
    }, [data?.actions?.viewFinishUrl]);

    useMetaTags({
        title: data?.meta?.title || t('app.title'),
        description: data?.meta?.description || t('app.description'),
        metas: [
            {name: 'keywords', content: t('app.keywords')}
        ],
    });

    const earnedPointsByCurrency = useMemo(() => {
        const source = data?.test?.completed ? (data?.test?.earnedPoints || []) : (testResult?.earnedPoints || []);
        return source.reduce((acc, item) => {
            acc[item.currencyCode] = item.amount;
            return acc;
        }, {});
    }, [data?.test?.completed, data?.test?.earnedPoints, testResult?.earnedPoints]);

    const submitTest = (event) => {
        event.preventDefault();

        setTestError(false);

        const formData = new FormData();
        Object.keys(selectedAnswers).forEach((questionId) => {
            formData.append(`question[${questionId}]`, selectedAnswers[questionId]);
        });

        fetch(data.actions.testSubmitUrl, {
            method: 'POST',
            credentials: 'same-origin',
            body: formData,
        })
            .then((response) => {
                if (!response.ok) {
                    setTestError(true);
                    return null;
                }

                return response.json();
            })
            .then((payload) => {
                if (payload) {
                    setTestResult(payload);
                    loadMissionCompleteFromCookie();
                }
            });
    };

    const buyUsingBonuses = () => {
        setBonusBuyError(false);

        fetch(data.actions.bonusBuyUrl, {
            method: 'POST',
            credentials: 'same-origin',
        }).then((response) => {
            if (response.ok) {
                window.location.reload();
                return;
            }

            setBonusBuyError(true);
        });
    };

    const buyMaterial = () => {
        if (!data?.actions?.buyUrl) {
            return;
        }

        window.location.href = data.actions.buyUrl;
    };

    if (!data) {
        if (notFound) {
            return <NotFoundPage />;
        }

        return null;
    }

    if (data.state === 'closed') {
        return (
            <section id="material" className="library-material-page">
                <div className="container">
                    <h1>{t('library.title')}</h1>
                    <p>{t('errorPages.general.header')}</p>
                    <Link to="/library">{t('library.backToSectionList')}</Link>
                </div>
            </section>
        );
    }

    const isTestCompleted = !!data?.test?.completed || !!testResult;
    const materialContentHtml = data.state === 'chargeable'
        ? (data?.material?.previewHtml || '')
        : (data?.material?.textHtml || '');

    return (
        <section id="material" className="library-material-page">
            {!!missionCompleteItems.length && (
                <div className="missions-done-block">
                    {missionCompleteItems.map((item, index) => (
                        <div className="missions-done-item" key={`${item.title}-${index}`}>
                            <strong>{item.title}</strong>
                        </div>
                    ))}
                </div>
            )}
            {!!data.promo?.backgroundImageUrl && (
                <div className="advertisement-cover" style={{backgroundImage: `url(${data.promo.backgroundImageUrl})`}}></div>
            )}

            <div className={`container${data.promo?.isPromoView ? ' wrap' : ''}`}>
                <div className="row">
                    <nav className="breadcrumbs">
                        <ul>
                            <li>
                                <Link to="/library">{t('library.backToSectionList')}</Link>
                            </li>
                            {!!data.section?.url && (
                                <li className="active">
                                    <Link to={data.section.url}>{data.section.title}</Link>
                                </li>
                            )}
                        </ul>
                    </nav>
                </div>

                {!!data.promo?.bannerImageUrls?.length && (
                    <div className="advertisement-slider">
                        <div className="js-material-advertisement-slick">
                            {data.promo.bannerImageUrls.map((url) => (
                                <div key={url}>
                                    <img src={url} alt=""/>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="material-grid">
                    <div>
                        {!!data.material.videoUrl && (
                            <div className="material-iframe">
                                <iframe className="material-video" title="material-video" src={data.material.videoUrl} frameBorder="0" allow="autoplay; encrypted-media" allowFullScreen></iframe>
                            </div>
                        )}

                        <div className="material-title-block flex">
                            <div className="title-wrap">
                                <h1>{data.material.title}</h1>
                            </div>
                            {!!data.sponsors?.length && (
                                <div className="brands-block">
                                    <div className="brands-logo">
                                        {data.sponsors.map((sponsor) => (
                                            <div key={`${sponsor.imageUrl}-${sponsor.url || ''}`}>
                                                {sponsor.url ? (
                                                    <a href={sponsor.url} target="_blank" rel="noreferrer">
                                                        <img src={sponsor.imageUrl} alt=""/>
                                                    </a>
                                                ) : (
                                                    <img src={sponsor.imageUrl} alt=""/>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="about-material">
                            {!!data.material.datePublish && (
                                <p>
                                    <span className="title">{t('library.material.datePublish')}</span> {data.material.datePublish}
                                </p>
                            )}
                            {!!data.about?.authors?.length && (
                                <p>
                                    <span className="title">{t('library.material.authors')}</span>{' '}
                                    {data.about.authors.map((author) => author.fullName).join(', ')}
                                </p>
                            )}
                            {!!data.about?.tags?.length && (
                                <p>
                                    <span className="title">{t('library.material.keywords')}</span>{' '}
                                    {data.about.tags.map((tag) => tag).join(', ')}
                                </p>
                            )}
                        </div>

                        <div className="material-content wysiwyg-content" dangerouslySetInnerHTML={{__html: materialContentHtml}}></div>

                        {data.state === 'chargeable' && (
                            <div className="material-chargeable-block promotional-code-block">
                                <h5>{t('library.material.payment.header')}</h5>
                                <div className="flex-container">
                                    <div className="column">
                                        {!!data.access?.pricePolicy && !data.access.pricePolicy.promoCodeOnly && (
                                            <div className="promo price">
                                                <div className="wrap">
                                                    <div className="flex-baseline">
                                                        <div>
                                                            <h6>{t('library.material.payment.price')}</h6>
                                                            {Number(data.access.pricePolicy.finalPrice) === Number(data.access.pricePolicy.marketingPrice) ? (
                                                                <p className="price">{data.access.pricePolicy.finalPrice} {t('app.currencies.uah')}</p>
                                                            ) : (
                                                                <>
                                                                    <p className="price-new">{data.access.pricePolicy.finalPrice} {t('app.currencies.uah')}</p>
                                                                    <p className="list-price">
                                                                        <span className="price-block">{data.access.pricePolicy.marketingPrice} {t('app.currencies.uah')}</span>
                                                                    </p>
                                                                </>
                                                            )}
                                                        </div>
                                                        <div className="buy-button-block">
                                                            <button className="btn" type="button" onClick={buyMaterial}>
                                                                {Number(data.access.pricePolicy.finalPrice) === 0
                                                                    ? t('courses.preview.payment.get')
                                                                    : t('courses.preview.payment.buy')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {!!data.access?.bonusPrice && (
                                            <div className="promo js-bonus-buy">
                                                <div className="wrap">
                                                    <div className="flex-baseline">
                                                        <div>
                                                            <h6>{t('library.material.payment.price')}</h6>
                                                            <p className="price">{data.access.bonusPrice} {t('app.payment.bonus')}</p>
                                                        </div>
                                                        <div className="right-align">
                                                            <button className="btn" type="button" onClick={buyUsingBonuses}>{t('app.payment.exchange')}</button>
                                                        </div>
                                                    </div>
                                                </div>
                                                {bonusBuyError && <div className="error-block">{t('app.payment.insufficientBonus')}</div>}
                                            </div>
                                        )}
                                    </div>
                                    <div className="column">
                                        {!data.isPublic && <PromoCode />}
                                        {data.isPublic && (
                                            <div className="material-auth-links">
                                                <Link to="/account/login">{t('buttons.login')}</Link>
                                                <Link to="/account/registration">{t('buttons.registration')}</Link>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {data.test?.exists && (
                            <div className="material-test">
                                <h2>{t('library.material.questions.caption')}</h2>
                                <div className="score-wrap">
                                    <span>{isTestCompleted ? t('library.material.questions.alreadyAnswered') : t('library.material.questions.maxScoreCaption')}</span>
                                    <div className="score-inline js-points">
                                        {(isTestCompleted ? (data.test?.maxPoints || []) : (data.test?.maxPoints || [])).map((point) => {
                                            const currencyCode = String(point.currencyCode || '').toLowerCase();
                                            const amount = isTestCompleted && earnedPointsByCurrency[point.currencyCode] !== undefined
                                                ? earnedPointsByCurrency[point.currencyCode]
                                                : point.amount;

                                            return (
                                                <a
                                                    key={point.currencyCode}
                                                    className={`score score-${currencyCode}`}
                                                    href={data?.meta?.url || '/library'}
                                                    onClick={(event) => event.preventDefault()}
                                                >
                                                    <p className={`js-${currencyCode}`}>{amount}</p>
                                                    <span>{point.currencyName || t(`app.currencies.${currencyCode}`)}</span>
                                                </a>
                                            );
                                        })}
                                    </div>
                                </div>

                                {data.test.canSubmit && !isTestCompleted && (
                                    <form className="js-material-test" onSubmit={submitTest}>
                                        {data.test.questions.map((question) => (
                                            <div className="form-block" key={question.id}>
                                                <h6>{question.text}</h6>
                                                <div className="js-required">
                                                    {question.answers.map((answer) => {
                                                        const checked = String(selectedAnswers[question.id] || '') === String(answer.id);
                                                        const isCorrect = !!testResult && String(testResult.questions?.[question.id]) === String(answer.id);

                                                        return (
                                                            <div className="form-item-block" key={answer.id}>
                                                                <input
                                                                    className="magic-radio"
                                                                    type="radio"
                                                                    name={`question-${question.id}`}
                                                                    id={`question-${question.id}-${answer.id}`}
                                                                    value={answer.id}
                                                                    checked={checked}
                                                                    onChange={() => {
                                                                        setSelectedAnswers((prev) => ({...prev, [question.id]: answer.id}));
                                                                    }}
                                                                />
                                                                <label htmlFor={`question-${question.id}-${answer.id}`} className={isCorrect ? 'tCh_true' : ''}>
                                                                    <span></span>{answer.text}
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                        {testError && <div className="error-block">{t('library.material.questions.requiredQuestionCaption')}</div>}
                                        <div className="button-block">
                                            <input type="submit" value={t('library.material.questions.submit')} className="btn btn-submit"/>
                                        </div>
                                    </form>
                                )}
                            </div>
                        )}
                    </div>

                    <div>
                        {!!data.relatedMaterials?.length && (
                            <>
                                <h5>{t('library.material.sameSectionMaterials')}</h5>
                                {data.relatedMaterials.map((material) => (
                                    <Link key={material.id} className="link-to-material" to={material.url}>
                                        <div className="material-of-section-grid">
                                            <div className="img-wrap">
                                                <img src={material.imageUrl} alt=""/>
                                            </div>
                                            <div className="material-of-section-title">
                                                <h6>{material.title}</h6>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
